/**
 * iter257 — Liste des conversations de la boîte mail (colonne de gauche).
 *
 * Navigation type Gmail / Apple Mail : onglets de pré-filtrage, avatars à
 * initiales, non lus en gras avec point vert, séparateurs de date, badges
 * métier (client reconnu, n° d'OR / de devis, pièce jointe) et action rapide
 * lu / non lu au survol.
 */
import { Badge } from "@/components/ui/badge";
import { Paperclip, MailOpen, Mail as MailIcon, Car, FileText, User } from "lucide-react";

const COLORS = [
  "bg-emerald-100 text-emerald-700", "bg-blue-100 text-blue-700",
  "bg-amber-100 text-amber-700", "bg-violet-100 text-violet-700",
  "bg-rose-100 text-rose-700", "bg-cyan-100 text-cyan-700",
];

const initials = (name, addr) => {
  const src = (name || addr || "?").trim();
  const parts = src.split(/[\s.@_-]+/).filter(Boolean);
  return ((parts[0]?.[0] || "?") + (parts[1]?.[0] || "")).toUpperCase();
};

const colorFor = (key) => {
  let h = 0;
  for (const ch of String(key || "")) h = (h * 31 + ch.charCodeAt(0)) % 997;
  return COLORS[h % COLORS.length];
};

const dayLabel = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const today = new Date();
  const startOf = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diffDays = Math.round((startOf(today) - startOf(d)) / 86400000);
  if (diffDays <= 0) return "Aujourd'hui";
  if (diffDays === 1) return "Hier";
  if (diffDays < 7) return "Cette semaine";
  if (diffDays < 31) return "Ce mois";
  return d.toLocaleDateString("fr-CH", { month: "long", year: "numeric" });
};

const shortTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const sameDay = d.toDateString() === new Date().toDateString();
  return sameDay
    ? d.toLocaleTimeString("fr-CH", { hour: "2-digit", minute: "2-digit" })
    : d.toLocaleDateString("fr-CH", { day: "2-digit", month: "2-digit" });
};

export const MAIL_TABS = [
  { value: "attention", label: "Attention" },
  { value: "clients", label: "Clients" },
  { value: "factures", label: "Factures" },
  { value: "or", label: "OR / Devis" },
  { value: "autres", label: "Autres" },
  { value: "tous", label: "Tous" },
];

export default function MailThreadList({ threads, selected, onOpen, onToggleSeen, loading }) {
  let lastDay = null;
  return (
    <div data-testid="mail-threads-container">
      {threads.length === 0 && !loading && (
        <div className="p-6 text-sm text-gray-400 text-center">Aucune conversation ici</div>
      )}
      {threads.map((t) => {
        const unread = t.unread > 0;
        const day = dayLabel(t.last_date);
        const showDay = day !== lastDay;
        lastDay = day;
        return (
          <div key={t.thread_key}>
            {showDay && (
              <div className="px-3 py-1 text-[10px] uppercase tracking-wider text-gray-400 bg-gray-50/80 border-b border-gray-100">
                {day}
              </div>
            )}
            <div
              className={`group relative border-b border-gray-100 ${
                selected === t.thread_key ? "bg-[#52a675]/10" : unread ? "bg-white" : "bg-white hover:bg-gray-50"
              }`}
            >
              <button
                type="button"
                onClick={() => onOpen(t.thread_key)}
                className="w-full text-left px-3 py-2.5 flex gap-2.5 min-w-0"
                data-testid={`mail-thread-${t.thread_key}`}
              >
                <div className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center text-[11px] font-bold ${colorFor(t.last_from_addr)}`}>
                  {initials(t.last_from_name, t.last_from_addr)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-2 min-w-0">
                    <span className={`text-sm truncate flex-1 min-w-0 ${unread ? "font-bold text-gray-900" : "text-gray-700"}`}>
                      {t.last_from_name || t.last_from_addr}
                    </span>
                    <span className="text-[10px] text-gray-400 flex-shrink-0">{shortTime(t.last_date)}</span>
                  </div>
                  <div className={`text-[13px] truncate ${unread ? "font-semibold text-gray-900" : "text-gray-600"}`}>
                    {t.subject}
                  </div>
                  <div className="text-xs text-gray-400 truncate">{t.last_preview}</div>
                  {(t.client || t.or_numero || t.devis_numero || t.has_attachments || t.count > 1) && (
                    <div className="flex items-center gap-1 mt-1 flex-wrap">
                      {t.client && (
                        <Badge variant="outline" className="h-4 px-1 text-[9px] bg-emerald-50 text-emerald-700 border-emerald-200 max-w-[130px]">
                          <User className="w-2.5 h-2.5 mr-0.5 flex-shrink-0" />
                          <span className="truncate">{t.client.label}</span>
                        </Badge>
                      )}
                      {t.or_numero && (
                        <Badge variant="outline" className="h-4 px-1 text-[9px] bg-blue-50 text-blue-700 border-blue-200">
                          <Car className="w-2.5 h-2.5 mr-0.5" />{t.or_numero}
                        </Badge>
                      )}
                      {t.devis_numero && (
                        <Badge variant="outline" className="h-4 px-1 text-[9px] bg-violet-50 text-violet-700 border-violet-200">
                          <FileText className="w-2.5 h-2.5 mr-0.5" />{t.devis_numero}
                        </Badge>
                      )}
                      {t.has_attachments && <Paperclip className="w-3 h-3 text-gray-400" />}
                      {t.count > 1 && <span className="text-[9px] text-gray-400">{t.count} messages</span>}
                    </div>
                  )}
                </div>
                {unread && <span className="w-2 h-2 rounded-full bg-[#52a675] flex-shrink-0 mt-2" />}
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onToggleSeen(t); }}
                title={unread ? "Marquer comme lu" : "Marquer comme non lu"}
                className="absolute right-1.5 bottom-1.5 p-1 rounded bg-white/90 border border-gray-200 text-gray-500 opacity-0 group-hover:opacity-100 hover:text-gray-900 transition-opacity"
                data-testid={`mail-toggle-seen-${t.thread_key}`}
              >
                {unread ? <MailOpen className="w-3.5 h-3.5" /> : <MailIcon className="w-3.5 h-3.5" />}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
